import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import { observer } from "mobx-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import Image from "@/components/image";
// hooks
import { useResponsive } from "@/hooks/use-responsive";
import { useSimplebarHeight } from "@/hooks/useSimplebarHeight";
// hooks
// components
import Logo from "@/components/logo";
import Scrollbar from "@/components/scrollbar";
import { usePathname } from "@/routes/hooks";
import { NavSectionVertical } from "@/components/nav-section";
import NavigationIdentifier from "@/components/navigation-identifier";
import { useGetDistrictById } from "../../hooks/useGetDistrictById";
import { useGetDistrictLogo } from "../../hooks/useGetDistrictLogo";
//
import { NAV } from "../config-layout";
import { useNavData } from "./config-navigation";

// ----------------------------------------------------------------------

const NavVertical = observer(({ openNav, onCloseNav }) => {
  const currentUser = useCurrentUser();

  const pathname = usePathname();
  const extractDistrictID = () => {
    const match = currentUser?.userPath?.match(/dis-[^#]+/);
    return match ? match[0] : null;
  };

  const { data: district } = useGetDistrictById(extractDistrictID());

  const { data: logo } = useGetDistrictLogo(district?.id, !!district);

  const lgUp = useResponsive("up", "lg");
  const navData = useNavData(currentUser);

  // Hook to read simplebar-content height
  const { height: simplebarHeight, scrollbarRef } = useSimplebarHeight({
    observe: true,
    debounceMs: 100,
  });

  // State to track window height
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // Calculate if screen fits content (simplebar height + 250px < window height)
  const screenFitsContent = simplebarHeight + 75 < windowHeight;

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  // Update window height on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Example: Log height changes and screen fit status (you can remove this in production)
  // useEffect(() => {
  //   console.log("Simplebar content height:", simplebarHeight);
  //   console.log("Window height:", windowHeight);
  //   console.log("Screen fits content:", screenFitsContent);
  // }, [simplebarHeight, windowHeight, screenFitsContent]);

  const renderContent = (
    <Scrollbar
      ref={scrollbarRef}
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4, mb: 1 }} />

      <NavSectionVertical
        data={navData}
        config={
          {
            // currentRole: user?.role || 'admin',
          }
        }
      />

      <Box
        sx={{
          position: screenFitsContent ? "absolute" : "relative",
          bottom: screenFitsContent ? 50 : 0,
          my: 2,
        }}
      >
        <NavigationIdentifier />
        {logo?.logo && (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            {logo.logo.trim().startsWith("<svg") ? (
              <Box sx={{ minHeight: "60px", maxHeight: "60px" }}>
                <ReactSVG
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(logo?.logo)}`}
                />
              </Box>
            ) : (
              <Image
                src={logo.logo}
                alt={`${district.name} logo`}
                sx={{ height: "60px" }}
              />
            )}
          </Box>
        )}
      </Box>
      {screenFitsContent && (
        <Box sx={{ height: logo?.logo ? "229.51px" : "137.951px" }} />
      )}
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      {/* <NavToggleButton /> */}

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: "fixed",
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
});

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  openNav: PropTypes.bool,
};

export default NavVertical;
